<template>
    <v-card class="ma-3 pa-5">
        <h3 class="calculation-title">Formulas</h3>

        <h4>Filters</h4>

        <v-simple-table dense class="filter-bg">
            <template v-slot:default>
                <tbody>
                    <tr>
                        <td>
                            <v-select
                                v-model="formulaFilter.formulaTarget"
                                :items="formulaTargets"
                                dense
                                hide-details
                                item-text="label"
                                item-value="value"
                                placeholder="Formula target"
                                clearable
                            />
                        </td>

                        <td>
                            <v-select
                                item-value="id"
                                item-text="name"
                                hide-details
                                dense
                                multiple
                                :items="soilTypes"
                                placeholder="Soil types"
                                v-model="formulaFilter.soilType"
                                clearable
                            >
                            </v-select>
                        </td>

                        <td>
                            <v-select
                                item-value="id"
                                item-text="code"
                                hide-details
                                dense
                                multiple
                                :items="countries"
                                placeholder="Countries"
                                v-model="formulaFilter.country"
                                clearable
                            >
                            </v-select>
                        </td>

                        <td>
                            <v-menu
                                class="date-to-menu"
                                v-model="formulaFilter.showStartDateMenu"
                                :close-on-content-click="false"
                                :nudge-right="40"
                                transition="scale-transition"
                                offset-y
                                min-width="auto"
                            >
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field
                                        class="date-to-menu"
                                        :value="formulaFilter.startDate ? formatDate(formulaFilter.startDate) : null"
                                        prepend-icon="mdi-calendar"
                                        readonly
                                        v-bind="attrs"
                                        v-on="on"
                                        clearable
                                        @click:clear="clearDate('start')"
                                    ></v-text-field>
                                </template>
                                <v-date-picker
                                    :show-current="false"
                                    first-day-of-week="1"
                                    v-model="formulaFilter.startDate"
                                    @input="formulaFilter.showStartDateMenu = false"
                                    clearable
                                ></v-date-picker>
                            </v-menu>
                            <v-menu
                                class="date-to-menu"
                                v-model="formulaFilter.showEndDateMenu"
                                :close-on-content-click="false"
                                :nudge-right="40"
                                transition="scale-transition"
                                offset-y
                                min-width="auto"
                            >
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field
                                        class="date-to-menu"
                                        :value="formulaFilter.endDate ? formatDate(formulaFilter.endDate) : null"
                                        prepend-icon="mdi-calendar"
                                        readonly
                                        v-bind="attrs"
                                        v-on="on"
                                        clearable
                                        @click:clear="clearDate('end')"
                                    ></v-text-field>
                                </template>
                                <v-date-picker
                                    :show-current="false"
                                    first-day-of-week="1"
                                    v-model="formulaFilter.endDate"
                                    @input="formulaFilter.showEndDateMenu = false"
                                ></v-date-picker>
                            </v-menu>
                        </td>
                    </tr>
                </tbody>
            </template>
        </v-simple-table>

        <v-simple-table dense>
            <template v-slot:default>
                <thead>
                    <tr>
                        <th class="text-left title-padding">
                            Index
                        </th>
                        <th class="text-left title-padding">
                            Formula
                        </th>
                        <th class="text-left">
                            Formula Target
                        </th>
                        <th class="text-left" v-if="!['no3'].includes(formulaTarget)">
                            Color
                        </th>
                        <th class="text-left" v-if="formulaTarget === 'dashboard'">
                            Soil types
                        </th>
                        <th class="text-left" v-if="['dashboard', 'no3'].includes(formulaTarget)">
                            Countries
                        </th>
                        <th class="text-left" v-if="['dashboard', 'no3'].includes(formulaTarget)">
                            Timespan
                        </th>
                        <th class="text-left" v-if="show_sensor_depth_selection">
                            Sensor
                        </th>
                        <th class="text-left">
                            Actions
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="formula in orderedFormulas" :key="formula.id">
                        <td class="index-textarea min-width-100 py-1">
                            <v-textarea
                                v-model="formula.index"
                                auto-grow
                                class="Index"
                                dense
                                flat
                                hide-details
                                placeholder="index"
                                rows="1"
                                solo
                            ></v-textarea>
                        </td>

                        <td class="min-width-400 py-1">
                            <v-textarea
                                v-model="formula.formula"
                                auto-grow
                                dense
                                flat
                                hide-details
                                placeholder="Formula"
                                rows="1"
                                solo
                            ></v-textarea>
                        </td>

                        <td class="base-min-width py-1">
                            <v-select
                                v-model="formula.formulaTarget"
                                :items="formulaTargets"
                                dense
                                hide-details
                                item-text="label"
                                item-value="value"
                                placeholder="Formula target"
                            />
                        </td>

                        <td class="base-min-width py-1" v-if="!['no3'].includes(formulaTarget)">
                            <v-select
                                v-model="formula.color"
                                :items="colors"
                                dense
                                hide-details
                                item-value="color"
                                item-text="title"
                                placeholder="Color"
                            />
                        </td>

                        <td class="base-min-width" v-if="formulaTarget === 'dashboard'">
                            <v-select
                                item-value="id"
                                item-text="name"
                                hide-details
                                dense
                                multiple
                                :items="soilTypes"
                                placeholder="Soil types"
                                v-model="formula.soilType"
                            />
                        </td>

                        <td class="base-min-width" v-if="['dashboard', 'no3'].includes(formulaTarget)">
                            <v-select
                                item-value="id"
                                item-text="code"
                                hide-details
                                dense
                                multiple
                                :items="countries"
                                placeholder="Countries"
                                v-model="formula.country"
                            />
                        </td>

                        <td class="base-min-width" v-if="['dashboard', 'no3'].includes(formulaTarget)">
                            <v-menu
                                class="date-to-menu"
                                v-model="formula.showStartDateMenu"
                                :close-on-content-click="false"
                                :nudge-right="40"
                                transition="scale-transition"
                                offset-y
                                min-width="auto"
                            >
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field
                                        class="date-to-menu date_style"
                                        :value="formatDate(formula.startDate)"
                                        prepend-icon="mdi-calendar"
                                        readonly
                                        v-bind="attrs"
                                        v-on="on"
                                    ></v-text-field>
                                </template>
                                <v-date-picker
                                    :show-current="false"
                                    first-day-of-week="1"
                                    v-model="formula.startDate"
                                    @input="formula.showStartDateMenu = false"
                                />
                            </v-menu>
                            <v-menu
                                class="date-to-menu"
                                v-model="formula.showEndDateMenu"
                                :close-on-content-click="false"
                                :nudge-right="40"
                                transition="scale-transition"
                                offset-y
                                min-width="auto"
                            >
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field
                                        class="date-to-menu"
                                        :value="formatDate(formula.endDate)"
                                        prepend-icon="mdi-calendar"
                                        readonly
                                        v-bind="attrs"
                                        v-on="on"
                                    ></v-text-field>
                                </template>
                                <v-date-picker
                                    :show-current="false"
                                    first-day-of-week="1"
                                    v-model="formula.endDate"
                                    @input="formula.showEndDateMenu = false"
                                ></v-date-picker>
                            </v-menu>
                        </td>

                        <td v-if="show_sensor_depth_selection" class="sensor-width py-1">
                            <v-checkbox
                                v-model="formula.sensorHigh"
                                :ripple="false"
                                dense
                                hide-details
                                label="High"
                            ></v-checkbox>
                            <v-checkbox
                                v-model="formula.sensorLow"
                                :ripple="false"
                                dense
                                hide-details
                                label="Low"
                            ></v-checkbox>
                        </td>

                        <td class="button-position base-min-width py-1">
                            <v-icon class="mr-2" title="Edit calculation" @click="handleEditFormulaClick(formula)">
                                mdi-content-save-outline
                            </v-icon>
                            <v-icon
                                class="mr-2"
                                title="Duplicate calculation"
                                @click="handleDuplicateFormulaClick(formula)"
                            >
                                mdi-content-duplicate
                            </v-icon>
                            <v-icon class="mr-2" title="Delete formula" @click="handleDeleteFormulaClick(formula.id)">
                                mdi-trash-can-outline
                            </v-icon>
                        </td>
                    </tr>
                </tbody>
            </template>
        </v-simple-table>
    </v-card>
</template>

<script>
import { mapActions, mapState, mapGetters } from "vuex";
import moment from "moment";

export default {
    name: "FormulaListTable",
    data() {
        return {
            orderedFormulas: []
        };
    },
    props: {
        show_sensor_depth_selection: {
            type: Boolean,
            default: false
        },
        formulaTarget: {
            type: String,
            default: "map"
        }
    },
    computed: {
        ...mapState("formula", ["colors", "formulaTargets", "formulas", "formulaFilter"]),
        ...mapState("soil", ["soilTypes"]),
        ...mapState("country", ["countries"]),
        ...mapGetters("formula", ["getFormulasOrderedByIndex", "getFilteredFormulas"])
    },
    watch: {
        formulaFilter: {
            handler() {
                this.orderedFormulas = this.getFilteredFormulas;
            },
            deep: true
        },
        formulas: function() {
            this.orderedFormulas = this.getFormulasOrderedByIndex;
        }
    },
    methods: {
        ...mapActions("formula", ["editFormula", "deleteFormula", "duplicateFormula", "clearFormulaFilterDate"]),
        async handleEditFormulaClick(formula) {
            await this.editFormula({ formula });
        },
        async handleDuplicateFormulaClick(formula) {
            const formulaClone = JSON.parse(JSON.stringify(formula));
            await this.duplicateFormula({ formula: formulaClone });
        },
        async handleDeleteFormulaClick(formulaId) {
            await this.deleteFormula({ formulaId });
        },
        formatDate(date) {
            if (date) {
                return moment(date).format("DD.MM.YY");
            }
            return "-";
        },
        clearDate(dateType) {
            this.clearFormulaFilterDate(dateType);
        }
    }
};
</script>

<style scoped>
.index-textarea {
    width: 8%;
}

.title-padding {
    padding-left: 27px !important;
}

.calculation-title {
    padding-bottom: 25px;
}

.button-position {
    display: table-cell;
    width: 165px;
}

.base-min-width {
    min-width: 165px;
}
.min-width-100 {
    min-width: 100px;
}
.min-width-400 {
    min-width: 400px;
}

.date_style {
    min-height: 0;
    height: 55px;
    padding: 10px 0;
}

.filter-bg {
    background-color: #f5f5f5;
}

.theme--light.v-data-table
    > .v-data-table__wrapper
    > table
    > tbody
    > tr:hover:not(.v-data-table__expanded__content):not(.v-data-table__empty-wrapper) {
    background-color: transparent;
}
</style>
